import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { Zap } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/features/auth';
import { useMatter, useMatterFilters, useMatters, useMatterMutations, useNpsMutation } from '../hooks';
import { GateTransitionDialog, MattersDataTable, NpsRatingDialog, CaseK3RatingDialog, QuickAdviceDialog } from '../components';
import { matterKeys } from '../api';
import { NEXT_PHASE } from '../types';
import type { Matter, MatterPhase } from '../types';

export function MattersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasAccess } = useAuth();
  const queryClient = useQueryClient();
  const { filters, updateFilter, updateFilters, resetFilters, setPage, setLimit, toggleSort } = useMatterFilters();

  const { data, isLoading } = useMatters(filters);
  const { deleteMutation } = useMatterMutations();
  const { npsMutation } = useNpsMutation();

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedMatter, setSelectedMatter] = React.useState<Matter | null>(null);
  const [npsDialogOpen, setNpsDialogOpen] = React.useState(false);
  const [caseK3DialogOpen, setCaseK3DialogOpen] = React.useState(false);
  const [quickDialogOpen, setQuickDialogOpen] = React.useState(false);

  // Quality Gate dialog (same flow as the detail page). The gate dialog
  // pre-fills inline fields (plan, compliance) from the FULL matter, so the
  // row's list payload isn't enough — the detail is fetched and the dialog
  // opens once it's loaded.
  const [gateMatterId, setGateMatterId] = React.useState<string | undefined>(undefined);
  const [gateToPhase, setGateToPhase] = React.useState<MatterPhase | undefined>(undefined);
  const [gateMode, setGateMode] = React.useState<'transition' | 'emergencyResolve'>('transition');
  const { data: gateMatter } = useMatter(gateMatterId);

  const handleEdit = (matter: Matter) => {
    navigate(`/app/matters/${matter.id}/edit`);
  };

  const handleDelete = (matter: Matter) => {
    setSelectedMatter(matter);
    setDeleteDialogOpen(true);
  };

  // Open the gate for the row's next lifecycle move (detail-page rules):
  // quick matter → close gate; PROVISIONAL_OPEN → emergency resolve;
  // else → advance to the legal next phase.
  const handleAdvance = (matter: Matter) => {
    const isQuickMatter =
      matter.category === 'QA' && !!matter.quickDeadline && matter.phase !== 'CLOSED';
    if (matter.phase === 'PROVISIONAL_OPEN') {
      setGateMode('emergencyResolve');
      setGateToPhase('PLANNING');
    } else if (isQuickMatter) {
      setGateMode('transition');
      setGateToPhase('CLOSED');
    } else {
      setGateMode('transition');
      setGateToPhase(matter.phase ? NEXT_PHASE[matter.phase] : undefined);
    }
    setGateMatterId(String(matter.id));
  };

  const confirmDelete = async () => {
    if (selectedMatter) {
      await deleteMutation.mutateAsync(selectedMatter.id);
      setDeleteDialogOpen(false);
      setSelectedMatter(null);
    }
  };

  const handleNps = (matter: Matter) => {
    setSelectedMatter(matter);
    setNpsDialogOpen(true);
  };

  const handleNpsSubmit = async (nps: number) => {
    if (selectedMatter) {
      await npsMutation.mutateAsync({ id: selectedMatter.id, nps });
      setNpsDialogOpen(false);
      const hasPeople =
        (selectedMatter.partners?.length ?? 0) > 0 ||
        (selectedMatter.responsibleAttorneys?.length ?? 0) > 0 ||
        (selectedMatter.staffs?.length ?? 0) > 0;
      if (hasPeople) {
        setCaseK3DialogOpen(true);
      } else {
        setSelectedMatter(null);
      }
    }
  };

  const handleNpsDialogOpenChange = (open: boolean) => {
    setNpsDialogOpen(open);
    if (!open) setSelectedMatter(null);
  };

  const handleRateTeam = (matter: Matter) => {
    setSelectedMatter(matter);
    setCaseK3DialogOpen(true);
  };

  const canCreate = hasAccess('matter', 'create');

  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = React.useState<HTMLDivElement | null>(null);

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('matters')}</h1>
        <div className="flex items-center gap-3">
          <div ref={setTableControlsEl} className="flex items-center gap-2" />
          {hasAccess('matter-practice-area', 'get') && (
            <Link to="/app/settings/matter-practice-areas">
              <Button variant="outline">{t('matterTypes')}</Button>
            </Link>
          )}
          {canCreate && (
            <Button
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={() => setQuickDialogOpen(true)}
            >
              <Zap className="mr-1.5 h-4 w-4" />
              {t('quickAdvice.newQuickAdvice')}
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => navigate('/app/matters/new')}>
              {t('newMatter')}
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <MattersDataTable
          controlsContainer={tableControlsEl}
          data={data?.results ?? []}
          total={data?.meta?.total ?? 0}
          filters={filters}
          isLoading={isLoading}
          onFiltersChange={updateFilter}
          onFiltersChangeBatch={updateFilters}
          onClearFilters={resetFilters}
          onPageChange={setPage}
          onLimitChange={setLimit}
          onSort={toggleSort}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onNps={handleNps}
          onRateTeam={handleRateTeam}
          onAdvance={handleAdvance}
          hasAccess={hasAccess}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteMatterConfirm', { name: selectedMatter?.name ?? selectedMatter?.id })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? t('deleting') : t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* NPS Rating Dialog (K2) */}
      <NpsRatingDialog
        open={npsDialogOpen}
        onOpenChange={handleNpsDialogOpenChange}
        matter={selectedMatter}
        onSubmit={handleNpsSubmit}
        isLoading={npsMutation.isPending}
      />

      {/* Case 360° — Quality Gate dialog for the row's next lifecycle move
          (a CLOSED target shows the result select + executes the close
          endpoint; success chains into the NPS rating, same as the detail page) */}
      <GateTransitionDialog
        open={!!gateMatterId && !!gateMatter}
        onOpenChange={(open) => {
          if (!open) {
            setGateMatterId(undefined);
            setGateToPhase(undefined);
            void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
          }
        }}
        matter={gateMatter ?? null}
        toPhase={gateMode === 'emergencyResolve' ? 'PLANNING' : gateToPhase}
        mode={gateMode}
        onSuccess={() => {
          void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
          if (gateMode === 'transition' && gateToPhase === 'CLOSED' && gateMatter) {
            setSelectedMatter(gateMatter);
            setNpsDialogOpen(true);
          }
        }}
      />

      {/* Quick Advice (QA) creation dialog */}
      <QuickAdviceDialog
        open={quickDialogOpen}
        onOpenChange={setQuickDialogOpen}
        onSuccess={() => {
          void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
        }}
      />

      {/* Case Team Rating Dialog (K3) */}
      <CaseK3RatingDialog
        open={caseK3DialogOpen}
        onOpenChange={(open) => {
          setCaseK3DialogOpen(open);
          if (!open) setSelectedMatter(null);
        }}
        matter={selectedMatter}
        onSuccess={() => {
          setSelectedMatter(null);
          void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
        }}
      />
    </div>
  );
}
